import { ReloadOutlined, RollbackOutlined } from "@ant-design/icons"
import {
  App,
  Button,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Steps,
  Tag,
  Typography,
} from "antd"
import { Alert } from "~/components/Alert"
import { useCallback, useEffect, useRef, useState } from "react"
import {
  applyOrderRefund,
  getOrderRefundDetail,
  refreshOrderRefundStatus,
  requestLevelOrderRefund,
  requestWalletOrderRefund,
} from "~/api/staff"
import { OrderType } from "~/common/constants"
import {
  applyStatusColor,
  applyStatusLabel,
  refundStatusColor,
  refundStatusLabel,
} from "./presentation"
import { formatAmount, formatDateTime, isAbortError } from "~/common/utils"
import { orderSourceLabel, orderTypeLabel } from "~/pages/Staff/labels"
import styles from "./index.css"

const { Text } = Typography

type RefundFormValues = {
  orderNo: string
}

type RefundAction = "request" | "refresh" | "apply" | null

const getWorkflowStep = (detail: API.Staff.OrderRefundDetailResponse) => {
  if (!detail.refund) return 0
  if (detail.refund.apply_status === "APPLIED") return 3
  if (detail.refund.status === "SUCCESS") return 2
  return 1
}

const getWorkflowAlert = (detail: API.Staff.OrderRefundDetailResponse) => {
  const refund = detail.refund

  if (!refund) {
    return detail.can_refund
      ? {
          type: "warning" as const,
          title: "订单可以发起退款",
          description: "请核对退款积点和退款金额后再继续。",
        }
      : {
          type: "error" as const,
          title: detail.blocked_reason || "当前订单不可退款",
          description: "订单详情仅供核对，当前没有可执行操作。",
        }
  }

  if (refund.apply_status === "APPLIED") {
    return {
      type: "success" as const,
      title: "退款及权益核销已完成",
      description: "退款单已经完成，不需要继续操作。",
    }
  }

  switch (refund.status) {
    case "SUCCESS":
      return {
        type: "warning" as const,
        title: "微信退款成功，等待核销权益",
        description: "请确认退款信息后执行最后一步权益核销。",
      }
    case "CLOSED":
    case "ABNORMAL":
      return {
        type: "error" as const,
        title: refundStatusLabel[refund.status],
        description: "该退款单已经进入终态，当前不能核销权益。",
      }
    default:
      return {
        type: "warning" as const,
        title: refundStatusLabel[refund.status],
        description: "请点击刷新按钮查询微信侧的最新退款状态。",
      }
  }
}

export const RefundModal = ({
  initialOrderNo,
  open,
  onClose,
  onChanged,
}: {
  initialOrderNo?: string
  open: boolean
  onClose: () => void
  onChanged: () => Promise<void>
}) => {
  const [form] = Form.useForm<RefundFormValues>()
  const { message } = App.useApp()
  const queryControllerRef = useRef<AbortController | undefined>(undefined)
  const [detail, setDetail] =
    useState<API.Staff.OrderRefundDetailResponse | null>(null)
  const [querying, setQuerying] = useState(false)
  const [activeAction, setActiveAction] = useState<RefundAction>(null)

  const loadDetail = useCallback(
    async (orderNo: string) => {
      const controller = new AbortController()
      queryControllerRef.current?.abort()
      queryControllerRef.current = controller
      setQuerying(true)

      try {
        const response = await getOrderRefundDetail(
          { order_no: orderNo },
          controller.signal,
        )
        if (controller.signal.aborted) return
        if (!response.data) {
          setDetail(null)
          void message.warning("退款详情为空，请稍后重试")
          return
        }
        setDetail(response.data)
      } catch (error) {
        if (!isAbortError(error)) {
          setDetail(null)
        }
      } finally {
        if (queryControllerRef.current === controller) setQuerying(false)
      }
    },
    [message],
  )

  useEffect(() => {
    if (!open) {
      queryControllerRef.current?.abort()
      form.resetFields()
      setDetail(null)
      setQuerying(false)
      setActiveAction(null)
      return
    }

    if (initialOrderNo) {
      form.setFieldValue("orderNo", initialOrderNo)
      void loadDetail(initialOrderNo)
    }

    return () => queryControllerRef.current?.abort()
  }, [form, initialOrderNo, loadDetail, open])

  const queryOrder = async () => {
    const values = await form.validateFields()
    await loadDetail(values.orderNo.trim())
  }

  const refreshAfterMutation = async (orderNo: string) => {
    await Promise.all([loadDetail(orderNo), onChanged()])
  }

  const requestRefund = async () => {
    if (!detail?.can_refund) return

    const orderNo = detail.order.order_no
    setActiveAction("request")
    try {
      const request =
        detail.order.order_type === OrderType.walletPackage
          ? requestWalletOrderRefund
          : requestLevelOrderRefund
      const response = await request({ order_no: orderNo })
      if (!response.data) {
        void message.error("退款申请结果为空，请稍后重试")
        return
      }
      void message.success("退款申请已提交")
      await refreshAfterMutation(orderNo)
    } finally {
      setActiveAction(null)
    }
  }

  const refreshRefund = async () => {
    const refund = detail?.refund
    if (!refund || !detail.can_refresh_refund_status) return

    setActiveAction("refresh")
    try {
      const response = await refreshOrderRefundStatus({
        refund_no: refund.refund_no,
      })
      if (!response.data) {
        void message.error("退款状态刷新结果为空，请稍后重试")
        return
      }
      void message.success("微信退款状态已刷新")
      await refreshAfterMutation(detail.order.order_no)
    } finally {
      setActiveAction(null)
    }
  }

  const applyRefund = async () => {
    const refund = detail?.refund
    if (!refund || !detail.can_apply_refund) return

    setActiveAction("apply")
    try {
      const response = await applyOrderRefund({ refund_no: refund.refund_no })
      if (!response.data) {
        void message.error("权益核销结果为空，请稍后重试")
        return
      }
      void message.success("退款权益已核销")
      await refreshAfterMutation(detail.order.order_no)
    } finally {
      setActiveAction(null)
    }
  }

  const actionBusy = activeAction !== null
  const alert = detail ? getWorkflowAlert(detail) : null
  const recharge = detail?.wallet_recharge_record
  const refund = detail?.refund

  return (
    <Modal
      destroyOnHidden
      footer={null}
      maskClosable={!actionBusy}
      open={open}
      title="处理订单退款"
      width={780}
      onCancel={() => {
        if (!actionBusy) onClose()
      }}
    >
      <Flex
        className={styles.modalContent}
        gap={20}
        vertical
      >
        <Form<RefundFormValues>
          form={form}
          onFinish={() => void queryOrder()}
        >
          <Flex gap={10}>
            <Form.Item
              className={styles.orderInput}
              name="orderNo"
              rules={[
                { required: true, message: "请输入订单号" },
                { max: 32, message: "订单号不能超过32个字符" },
              ]}
            >
              <Input
                allowClear
                disabled={actionBusy}
                placeholder="请输入订单号"
                onChange={() => {
                  queryControllerRef.current?.abort()
                  setDetail(null)
                }}
              />
            </Form.Item>
            <Button
              disabled={actionBusy}
              htmlType="submit"
              loading={querying}
              type="primary"
            >
              查询
            </Button>
          </Flex>
        </Form>

        {detail ? (
          <>
            <Steps
              current={getWorkflowStep(detail)}
              items={[
                { title: "发起退款" },
                { title: "确认微信退款" },
                { title: "核销权益" },
              ]}
              status={
                refund?.status === "CLOSED" || refund?.status === "ABNORMAL"
                  ? "error"
                  : "process"
              }
            />

            {alert ? (
              <Alert
                description={alert.description}
                showIcon
                title={alert.title}
                type={alert.type}
              />
            ) : null}

            <Descriptions
              bordered
              column={2}
              size="small"
              title="订单与退款信息"
            >
              <Descriptions.Item label="订单号">
                <Text copyable>{detail.order.order_no}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="订单类型">
                {orderTypeLabel[detail.order.order_type]}
              </Descriptions.Item>
              <Descriptions.Item label="订单来源">
                {orderSourceLabel[detail.order.source]}
              </Descriptions.Item>
              <Descriptions.Item label="订单金额">
                {formatAmount(detail.order.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="原始积点">
                {recharge?.recharge_amount ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="剩余积点">
                {recharge?.remaining_amount ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="已使用积点">
                {recharge?.used_amount ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="冻结积点">
                <Text type={detail.frozen_amount > 0 ? "danger" : undefined}>
                  {detail.frozen_amount}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="退款积点">
                {detail.refund_points}
              </Descriptions.Item>
              <Descriptions.Item label="退款金额">
                <Text strong>{formatAmount(detail.refund_amount)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="商户退款单号">
                {refund ? <Text copyable>{refund.refund_no}</Text> : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="微信退款状态">
                {refund ? (
                  <Tag color={refundStatusColor[refund.status]}>
                    {refundStatusLabel[refund.status]}
                  </Tag>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="权益核销状态">
                {refund ? (
                  <Tag color={applyStatusColor[refund.apply_status]}>
                    {applyStatusLabel[refund.apply_status]}
                  </Tag>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="核销时间">
                {formatDateTime(refund?.applied_at)}
              </Descriptions.Item>
            </Descriptions>

            <Flex
              className={styles.modalActions}
              gap={10}
              justify="flex-end"
              wrap
            >
              {detail.can_refund ? (
                <Popconfirm
                  description={`将退款 ${detail.refund_points} 积点对应的 ${formatAmount(detail.refund_amount)}。`}
                  okButtonProps={{ danger: true }}
                  okText="确认发起"
                  title="确认发起微信退款？"
                  onConfirm={() => requestRefund()}
                >
                  <Button
                    danger
                    icon={<RollbackOutlined />}
                    loading={activeAction === "request"}
                    type="primary"
                  >
                    发起微信退款
                  </Button>
                </Popconfirm>
              ) : null}
              {detail.can_refresh_refund_status ? (
                <Button
                  icon={<ReloadOutlined />}
                  loading={activeAction === "refresh"}
                  onClick={() => void refreshRefund()}
                >
                  刷新微信退款状态
                </Button>
              ) : null}
              {detail.can_apply_refund ? (
                <Popconfirm
                  description={
                    detail.order.order_type === OrderType.levelPackage
                      ? "将收回退款积点并立即结束用户专业版权益。"
                      : "将收回本次退款对应的剩余积点。"
                  }
                  okButtonProps={{ danger: true }}
                  okText="确认核销"
                  title="确认核销本地权益？"
                  onConfirm={() => applyRefund()}
                >
                  <Button
                    danger
                    loading={activeAction === "apply"}
                    type="primary"
                  >
                    核销退款权益
                  </Button>
                </Popconfirm>
              ) : null}
            </Flex>
          </>
        ) : null}
      </Flex>
    </Modal>
  )
}
